import {
  BarChart,
  ChatOutlined,
  ClearAllOutlined,
  CloudSync,
  DeleteOutlineOutlined,
  Description,
  EditOutlined,
  FileUploadOutlined,
  LaunchOutlined,
  MoreVert,
  Person,
  PersonAddAlt1Outlined,
  Schedule,
  SettingsOutlined,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  SyncLock,
  SyncProblem,
  VisibilityOutlined,
} from "@mui/icons-material";
import {
  Box,
  ButtonGroup,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import { DatasetButton } from "src/reusable/components/DatasetButtons";
import {
  ClearDatasetModal,
  DatasetFolderMappingModal,
  UpdateDatasetFolderModal,
} from "src/views/forms/DatasetModals";
import {
  useStarDataset,
  useSyncRemoteDataset,
  useSyncStatus,
  useUnstarDataset,
} from "../services/DatasetServices";
import DatasetDownloadAllButton from "./DatasetDownloadAllButton";

import DriveFileMoveIcon from "@mui/icons-material/DriveFileMove";
import FolderIcon from "@mui/icons-material/Folder";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PublicIcon from "@mui/icons-material/Public";
import PublicOffIcon from "@mui/icons-material/PublicOff";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
// import Divider from "@mui/material/Divider"; // TODO: Remove if not needed
import { useHistory } from "react-router-dom";
import { nuclearn_api_url } from "src/App";
import { Can, usePermission } from "src/helpers/Auth";
import { useGetUserDetails } from "src/helpers/UserDetails";
import ShareDialog from "src/reusable/components/ShareDialog";
import { PowerBIDataModal } from "src/reusable/components/PowerBIComponents";
import { NucToast } from "src/reusable/components/Toast";
import { useChatStore } from "src/views/assistant/stores/ChatStore";
import { DeleteDatasetModal } from "src/views/forms/datasetModels/DeleteDatasetModal";
import { UpdateDatasetModal } from "src/views/forms/datasetModels/UpdateDatasetModal";
import DatasetCardActionMenu from "./DatasetCardActionsMenu";
import DatasetTypeChip from "./DatasetTypeChip";

type DatasetCardProps = {
  // data can be either a dataset or a dataset folder
  data: any;
  folderId?: number;
  atomResults?: boolean;
};
const formatDate = (value: string) => {
  if (!value) return "";
  const date = new Date(value);
  return new Intl.DateTimeFormat(navigator.language, {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(date);
};
const DatasetCard = ({
  data: dataset,
  folderId,
  atomResults = false,
}: DatasetCardProps) => {
  const history = useHistory();
  const theme = useTheme();

  const navigateToDataset = () => {
    if (dataset?.datasets) {
      return history.push(`/datasets/dataset-folders/${dataset?.id}`, {
        folderId: folderId,
        datasetFolder: dataset,
      });
    }

    const effectiveFolderId = folderId ?? dataset?.dataset_folder_fk;
    if (effectiveFolderId) {
      return history.push(
        `/datasets/dataset-folders/${effectiveFolderId}/dataset/${dataset?.id}/viewer`,
      );
    }
    return history.push(`/datasets/${dataset?.id}/viewer`);
  };
  const cardStyle = {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    ...(dataset.datasets && {
      "&:hover": {
        backgroundColor: theme.palette.divider,
      },
      width: "100%",
      border: 1,
      borderColor: "grey.200",
      borderRadius: 1,
      cursor: "pointer",
      // m: 1,
      // p: 1,
    }),
  };

  return (
    <>
      <Card
        sx={cardStyle}
        variant="outlined"
        id={`dataset-card-${dataset?.name}`}
        data-testid={`ds-card-${dataset?.id}`}
        data-attr={`datasets:card:dataset-${dataset?.id}`}
      >
        <CardHeader
          title={
            <Box
              display="flex"
              alignItems="center"
              gap={1.5}
              onClick={navigateToDataset}
              sx={{ cursor: "pointer", overflow: "hidden" }} // overflow hidden here
            >
              {dataset?.datasets ? (
                <FolderIcon color="primary" />
              ) : (
                <DatasetTypeChip type={dataset?.dataset_type!?.name} avatar />
              )}
              {!folderId ? (
                <Typography
                  variant={folderId ? "body1" : "h6"}
                  fontWeight="bold"
                  data-testid={`ds-card-name-${dataset?.id}`}
                  sx={{
                    cursor: "pointer",
                    "&:hover": { textDecoration: "underline" },
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {dataset?.name}
                </Typography>
              ) : (
                <Tooltip title={dataset?.name} enterDelay={700}>
                  <Typography
                    variant={folderId ? "body1" : "h6"}
                    fontWeight="bold"
                    data-testid={`ds-card-name-${dataset?.id}`}
                    sx={{
                      cursor: "pointer",
                      "&:hover": { textDecoration: "underline" },
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {dataset?.name}
                  </Typography>
                </Tooltip>
              )}
            </Box>
          }
          action={
            <Box sx={{ flexShrink: 0 }}>
              <DatasetCardMoreAction
                dataset={dataset}
                isFolder={Boolean(dataset?.datasets)}
                atomResults={atomResults}
              />
            </Box>
          }
          sx={{ "& .MuiCardHeader-content": { overflow: "hidden" } }}
        />
        <CardContent
          sx={{ pt: 0, flex: 1, width: "100%" }}
          hidden={Boolean(dataset.datasets)}
        >
          <DatasetCardInfo data={dataset} folderId={folderId} />
          <Typography
            id={`dataset-description-${dataset?.name}`}
            variant="body2"
            color="textSecondary"
            component="p"
          >
            {dataset?.description}
          </Typography>
        </CardContent>
        <CardActions sx={{ display: "flex" }} hidden={dataset?.datasets}>
          <Box>
            <DatasetCardButtons dataset={dataset} folderId={folderId} />
          </Box>
        </CardActions>
      </Card>
    </>
  );
};
const DatasetCardInfo = ({ data: dataset }: DatasetCardProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const currentUser = useGetUserDetails();
  const ownerId = dataset?.create_user_fk ?? dataset?.create_user?.id;
  const ownedByMe = currentUser?.id !== undefined && ownerId === currentUser.id;
  // Folder payloads carry is_public; dataset payloads carry visibility_level.
  const isPrivate = dataset?.datasets
    ? dataset?.is_public === false
    : dataset?.visibility_level === "private";
  return (
    <>
      <Stack
        direction={isMobile ? "column" : "row"}
        alignItems={isMobile ? "flex-start" : "center"}
        mt={1}
        gap={isMobile ? 0.5 : 1.5}
        flexWrap="wrap"
      >
        <Box display="flex" alignItems="center" gap={0.8}>
          <Person fontSize="small" color="action" />
          <Typography variant="body2" color="textSecondary">
            {ownedByMe ? "You" : dataset?.create_user?.user_display_name}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={0.8}>
          <Schedule fontSize="small" color="action" />
          <Typography variant="body2" color="textSecondary">
            {formatDate(dataset?.create_date)}
          </Typography>
        </Box>
        {!dataset?.datasets && (
          <Box display="flex" alignItems="center" gap={0.8}>
            <Description fontSize="small" color="action" />
            <Typography variant="body2" color="textSecondary">
              {(dataset.sdr_count ?? 0).toLocaleString()}{" "}
              {dataset.sdr_count === 1 ? "record" : "records"}
            </Typography>
          </Box>
        )}
        {dataset?.star_count !== undefined && dataset?.star_count > 0 && (
          <Box display="flex" alignItems="center" gap={0.8}>
            <StarIcon fontSize="small" sx={{ color: "warning.main" }} />
            <Typography variant="body2" color="textSecondary">
              {dataset.star_count} {dataset.star_count === 1 ? "star" : "stars"}
            </Typography>
          </Box>
        )}
        <Box display="flex" alignItems="center" gap={0.8}>
          {isPrivate ? (
            <PublicOffIcon fontSize="small" color="action" />
          ) : (
            <PublicIcon fontSize="small" color="action" />
          )}
          <Typography variant="body2" color="textSecondary">
            {isPrivate ? "Private" : "Public"}
          </Typography>
        </Box>
      </Stack>
      <Stack direction="row" flexWrap="wrap" gap={1} my={1.5}>
        {dataset?.tags?.map((tag: any) => (
          <Box
            key={tag.id}
            sx={{
              bgcolor: alpha(theme.palette.secondary.main, 0.1),
              color: theme.palette.secondary.main,
              px: 1.5,
              py: 0.5,
              borderRadius: "16px",
            }}
          >
            {tag.name}
          </Box>
        ))}
      </Stack>
    </>
  );
};
type DatasetCardButtonsProps = {
  dataset: any;
  folderId?: number;
};
const DatasetCardButtons = ({ dataset, folderId }: DatasetCardButtonsProps) => {
  const [openPowerBIModal, setOpenPowerBIModal] = useState(false);
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  // Per-dataset ACL riding on the search payload. Explicit-deny semantics:
  // only a present-and-false can_write disables (payloads without the flags,
  // e.g. folder listings, must not false-disable); the upload page enforces
  // the strict check.
  const writeDenied = dataset?.my_access?.can_write === false;
  const { setDatasets } = useChatStore();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const open = Boolean(anchorEl);

  // Track sync task
  const [syncTaskId, setSyncTaskId] = useState<string | null>(null);

  console.log("DatasetCard render:", {
    datasetId: dataset?.id,
    datasetName: dataset?.name,
    syncTaskId,
    isRemoteDataset: dataset?.remote_dataset,
  });

  const {
    mutate: syncRemoteDataset,
    isError: isSyncError,
    isSuccess: isSyncSuccess,
    data: syncMutationData,
  } = useSyncRemoteDataset();

  // Extract task_id from mutation result
  useEffect(() => {
    if (syncMutationData && syncMutationData.task_id && !syncTaskId) {
      console.log("Setting task ID from mutation data:", syncMutationData);
      setSyncTaskId(syncMutationData.task_id);
    }
  }, [syncMutationData, syncTaskId]);

  // Poll sync status when we have a task ID
  const { data: syncStatus } = useSyncStatus(dataset?.id, syncTaskId, {
    enabled: !!syncTaskId,
    onSuccess: (data: any) => {
      console.log("Sync status update:", data);
    },
  });

  // Handle sync completion
  useEffect(() => {
    console.log("Sync completion check:", { syncStatus, syncTaskId });
    if (syncStatus?.state === "SUCCESS") {
      NucToast({
        message: "Dataset synced successfully",
        type: "success",
        hideProgressBar: false,
        // @ts-ignore
        toastId: "sync-dataset-success",
      });
      setSyncTaskId(null); // Clear task ID to stop polling
    } else if (syncStatus?.state === "FAILURE") {
      NucToast({
        message: `Dataset Sync Failed: ${syncStatus?.error || "Unknown error"}`,
        type: "error",
        hideProgressBar: false,
        // @ts-ignore
        toastId: "sync-dataset-failed",
      });
      setSyncTaskId(null); // Clear task ID to stop polling
    }
  }, [syncStatus]);

  useEffect(() => {
    if (isSyncSuccess) {
      NucToast({
        message: "Dataset synced successfully",
        type: "success",
        hideProgressBar: false,
        // @ts-ignore
        toastId: "sync-dataset-success",
      });
    }
  }, [isSyncSuccess]);
  useEffect(() => {
    if (isSyncError) {
      NucToast({
        message: "Dataset Sync Failed",
        type: "error",
        hideProgressBar: false,
        // @ts-ignore
        toastId: "sync-dataset-failed",
      });
    }
  }, [isSyncError]);
  const isDocumentType = (type: string) => {
    return type === "document";
  };
  const handleUploadToDataset = () => {
    history.push(`/datasets/${dataset?.id}/upload`);
  };
  const handleAnalyzeClick = () => {
    history.push(`/datasets/${dataset?.id}/stats`);
  };
  const handleOpenPowerBIModal = () => {
    setOpenPowerBIModal(true);
  };
  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const navigateToDataset = () => {
    const effectiveFolderId = folderId ?? dataset?.dataset_folder_fk;
    if (effectiveFolderId) {
      return history.push(
        `/datasets/dataset-folders/${effectiveFolderId}/dataset/${dataset?.id}/viewer`,
      );
    }
    return history.push(`/datasets/${dataset?.id}/viewer`);
  };

  const handleChatWithDataset = () => {
    // Set the dataset in the AtomAssist store
    setDatasets([
      {
        id: dataset.id,
        name: dataset.name,
        description: dataset.description,
      },
    ]);

    // Set an initial message to guide the user
    // TODO: This was broken but merging to get the core feature in
    // setInitialUserMessage(
    //   `I'd like to chat about the dataset: ${dataset.name}`
    // );

    // Navigate to AtomAssist with dataset info in state
    history.push({
      pathname: "/assistant",
      state: {
        preloadDataset: {
          id: dataset.id,
          name: dataset.name,
          description: dataset.description,
        },
      },
    });
  };

  // Mobile: Show only View + Chat as icon buttons, rest in menu
  if (isMobile) {
    return (
      <Box display="flex" gap={0.5} alignItems="center">
        <Can
          perform="dataset_data:read"
          yes={() => (
            <Tooltip title="View">
              <IconButton
                id={`view-dataset-${dataset?.name}`}
                data-testid={`ds-view-btn-${dataset?.id}`}
                data-attr={`datasets:btn:view-dataset-${dataset?.id}`}
                onClick={navigateToDataset}
                color="primary"
                size="small"
              >
                <VisibilityOutlined />
              </IconButton>
            </Tooltip>
          )}
        />
        <Can
          perform="dataset_data:read"
          yes={() => (
            <Tooltip title="Chat">
              <IconButton
                id={`chat-dataset-${dataset?.name}`}
                data-testid={`ds-chat-btn-${dataset?.id}`}
                data-attr={`datasets:btn:chat-with-dataset-${dataset?.id}`}
                onClick={handleChatWithDataset}
                color="primary"
                size="small"
              >
                <ChatOutlined />
              </IconButton>
            </Tooltip>
          )}
        />
        <Can
          perform="dataset_data:write"
          yes={() => (
            <Tooltip
              title={
                writeDenied
                  ? `You do not have permission to ${dataset?.remote_dataset ? "sync" : "upload to"} this dataset`
                  : dataset?.remote_dataset
                    ? "Sync"
                    : "Upload"
              }
            >
              {/* inline-flex so this wrapper (needed for tooltips on
                  disabled buttons) is layout-neutral: it sizes exactly to
                  the button like the unwrapped sibling icons. */}
              <span style={{ display: "inline-flex" }}>
                <IconButton
                  id={`upload-dataset-${dataset?.name}`}
                  data-attr={`datasets:btn:${dataset?.remote_dataset ? "sync" : "upload"}-dataset-${dataset?.id}`}
                  onClick={
                    dataset?.remote_dataset
                      ? () => syncRemoteDataset(dataset?.id)
                      : handleUploadToDataset
                  }
                  color="primary"
                  size="small"
                  disabled={writeDenied || !!syncTaskId}
                >
                  {dataset?.remote_dataset ? (
                    <CloudSync />
                  ) : (
                    <FileUploadOutlined />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}
        />
        <Can
          perform="dataset_data:read"
          yes={() => (
            <DatasetDownloadAllButton
              datasetId={dataset?.id}
              datasetType={dataset?.dataset_type}
              datasetName={dataset?.name}
              iconOnly
            />
          )}
        />
        <IconButton
          onClick={(e) => setMobileMenuAnchor(e.currentTarget)}
          size="small"
        >
          <MoreVert />
        </IconButton>
        <Menu
          anchorEl={mobileMenuAnchor}
          open={Boolean(mobileMenuAnchor)}
          onClose={() => setMobileMenuAnchor(null)}
        >
          {!isDocumentType(dataset?.dataset_type?.name) && (
            <>
              <Can
                perform="dataset_data:read"
                yes={() => (
                  <MenuItem
                    onClick={() => {
                      handleOpenPowerBIModal();
                      setMobileMenuAnchor(null);
                    }}
                  >
                    <LaunchOutlined sx={{ mr: 1 }} fontSize="small" />
                    Power BI
                  </MenuItem>
                )}
              />
              <Can
                perform="dataset_data:read"
                yes={() => (
                  <MenuItem
                    onClick={() => {
                      handleAnalyzeClick();
                      setMobileMenuAnchor(null);
                    }}
                  >
                    <BarChart sx={{ mr: 1 }} fontSize="small" />
                    Analyze
                  </MenuItem>
                )}
              />
            </>
          )}
        </Menu>
        <PowerBIDataModal
          url={`${nuclearn_api_url}/datasets/${dataset?.id}/data`}
          message={` to dataset "${dataset?.name}"`}
          forPage={"datasets"}
          isVisible={openPowerBIModal}
          onClose={() => setOpenPowerBIModal(false)}
        />
        <Can
          perform="dataset:update"
          yes={() => (
            <DatasetCardActionMenu
              isOpen={open}
              anchorEl={anchorEl}
              setAnchorEl={setAnchorEl}
              dataset={dataset}
            />
          )}
        />
      </Box>
    );
  }

  // Desktop: Full button layout
  return (
    <Box display="flex" gap={0.5} flexWrap="wrap">
      <ButtonGroup
        variant="outlined"
        size="small"
        color="primary"
        aria-label="Basic button group"
        sx={{ flexWrap: "wrap" }}
      >
        <Can
          perform="dataset_data:write"
          yes={() =>
            !dataset?.remote_dataset ? (
              writeDenied ? (
                <Tooltip title="You do not have permission to upload to this dataset">
                  <span style={{ display: "inline-flex" }}>
                    <DatasetButton
                      id={`upload-dataset-${dataset?.name}`}
                      data-testid={`ds-btn-upload-${dataset?.id}`}
                      startIcon={<FileUploadOutlined />}
                      disabled
                      sx={{ flex: 1 }}
                    >
                      Upload
                    </DatasetButton>
                  </span>
                </Tooltip>
              ) : (
                <DatasetButton
                  id={`upload-dataset-${dataset?.name}`}
                  data-testid={`ds-btn-upload-${dataset?.id}`}
                  data-attr={`datasets:btn:upload-to-dataset-${dataset?.id}`}
                  startIcon={<FileUploadOutlined />}
                  onClick={handleUploadToDataset}
                >
                  Upload
                </DatasetButton>
              )
            ) : (
              <DatasetButton
                id={`sync-dataset-${dataset?.name}`}
                data-testid={`ds-btn-sync-${dataset?.id}`}
                data-attr={`datasets:btn:sync-dataset-${dataset?.id}`}
                startIcon={
                  isSyncSuccess ? (
                    <SyncLock />
                  ) : isSyncError ? (
                    <SyncProblem />
                  ) : (
                    <CloudSync />
                  )
                }
                onClick={() => {
                  console.log("Sync button clicked for dataset:", dataset?.id);
                  syncRemoteDataset(dataset?.id);
                }}
                disabled={writeDenied || !!syncTaskId}
              >
                {syncTaskId ? (
                  <Box sx={{ width: "100%", minWidth: "80px" }}>
                    <Typography variant="caption" display="block">
                      {syncStatus?.progress || 0}%
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={syncStatus?.progress || 0}
                      sx={{ height: 2, borderRadius: 1 }}
                    />
                    {syncStatus?.message && (
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{
                          fontSize: "0.65rem",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {syncStatus.message}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  "Sync"
                )}
              </DatasetButton>
            )
          }
        />
        <Can
          perform="dataset_data:read"
          yes={() => (
            <DatasetDownloadAllButton
              datasetId={dataset?.id}
              datasetType={dataset?.dataset_type}
              datasetName={dataset?.name}
              buttonVariant="dataset"
            />
          )}
        />
        <Can
          perform="dataset_data:read"
          yes={() => (
            <DatasetButton
              id={`power-bi-dataset-${dataset?.name}`}
              data-testid={`ds-btn-powerbi-${dataset?.id}`}
              data-attr={`datasets:btn:open-powerbi-${dataset?.id}`}
              startIcon={<LaunchOutlined />}
              onClick={handleOpenPowerBIModal}
              hidden={isDocumentType(dataset?.dataset_type?.name)}
            >
              Power BI
            </DatasetButton>
          )}
        />
        <Can
          perform="dataset_data:read"
          yes={() => (
            <DatasetButton
              id={`analyze-dataset-${dataset?.name}`}
              data-testid={`ds-btn-analyze-${dataset?.id}`}
              data-attr={`datasets:btn:analyze-dataset-${dataset?.id}`}
              startIcon={<BarChart />}
              hidden={isDocumentType(dataset?.dataset_type?.name)}
              onClick={handleAnalyzeClick}
            >
              Analyze
            </DatasetButton>
          )}
        />
        <Can
          perform="dataset_data:read"
          yes={() => (
            <DatasetButton
              id={`chat-dataset-${dataset?.name}`}
              data-testid={`ds-chat-btn-${dataset?.id}`}
              data-attr={`datasets:btn:chat-with-dataset-${dataset?.id}`}
              startIcon={<ChatOutlined />}
              onClick={handleChatWithDataset}
            >
              Chat
            </DatasetButton>
          )}
        />
        <Can
          perform="dataset_data:read"
          yes={() => (
            <DatasetButton
              id={`view-dataset-${dataset?.name}`}
              data-testid={`ds-view-btn-${dataset?.id}`}
              data-attr={`datasets:btn:view-dataset-${dataset?.id}`}
              startIcon={<VisibilityOutlined />}
              onClick={navigateToDataset}
            >
              View
            </DatasetButton>
          )}
        />
      </ButtonGroup>
      <PowerBIDataModal
        url={`${nuclearn_api_url}/datasets/${dataset?.id}/data`}
        message={` to dataset "${dataset?.name}"`}
        forPage={"datasets"}
        isVisible={openPowerBIModal}
        onClose={() => setOpenPowerBIModal(false)}
      />
      <Can
        perform="dataset:update"
        yes={() => (
          <>
            <IconButton
              onClick={handleOpenMenu}
              id={`icon-menu-${dataset?.name}`}
              data-testid={`ds-btn-actions-${dataset?.id}`}
              data-attr={`datasets:menu:more-actions-${dataset?.id}`}
            >
              <MoreVert />
            </IconButton>
            <DatasetCardActionMenu
              isOpen={open}
              anchorEl={anchorEl}
              setAnchorEl={setAnchorEl}
              dataset={dataset}
            />
          </>
        )}
      />
    </Box>
  );
};
// NOTE: DatasetNoFolder and EllipsisButton must stay at module scope. When
// they were defined inside DatasetCardMoreAction, every parent re-render
// produced a new component type, so React remounted the whole subtree —
// visibly flashing any open modal and wiping its state (e.g. the delete
// confirmation text).

type DatasetModalControls = {
  openUpdateModal: boolean;
  setOpenUpdateModal: (open: boolean) => void;
  openClearModal: boolean;
  setOpenClearModal: (open: boolean) => void;
  openDeleteModal: boolean;
  setOpenDeleteModal: (open: boolean) => void;
};

type DatasetNoFolderProps = DatasetModalControls & {
  dataset: any;
  atomResults?: boolean;
  openFolderMappingModal: boolean;
  setOpenFolderMappingModal: (open: boolean) => void;
  onStarClick: (e: React.MouseEvent) => void;
  onMoveToFolder: () => void;
  onEdit: () => void;
  onClear: () => void;
  onDelete: () => void;
};

const DatasetNoFolder = ({
  dataset,
  atomResults = false,
  openUpdateModal,
  setOpenUpdateModal,
  openClearModal,
  setOpenClearModal,
  openDeleteModal,
  setOpenDeleteModal,
  openFolderMappingModal,
  setOpenFolderMappingModal,
  onStarClick,
  onMoveToFolder,
  onEdit,
  onClear,
  onDelete,
}: DatasetNoFolderProps) => {
  return (
    <Box>
      <Stack direction="row" alignItems="center" gap={0}>
        <Tooltip
          title={
            dataset?.is_starred_by_user
              ? "Remove from favorites"
              : "Add to favorites"
          }
          arrow
        >
          <IconButton
            id={`star-dataset-${dataset?.name}`}
            data-testid={`ds-btn-star-${dataset?.id}`}
            data-attr={`datasets:btn:toggle-star-${dataset?.id}`}
            size="small"
            onClick={onStarClick}
            sx={{
              color: dataset?.is_starred_by_user
                ? "warning.main"
                : "action.active",
            }}
          >
            {dataset?.is_starred_by_user ? (
              <StarIcon fontSize="small" />
            ) : (
              <StarBorderIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
        <Can
          perform="dataset:update"
          yes={() => (
            <Tooltip
              title="Move To Folder"
              arrow
              hidden={dataset?.remote_dataset}
            >
              <IconButton
                id={`edit-dataset-${dataset?.name}`}
                data-testid={`ds-btn-move-${dataset?.id}`}
                data-attr={`datasets:btn:move-to-folder-${dataset?.id}`}
                size="small"
                onClick={onMoveToFolder}
                hidden={atomResults}
              >
                <DriveFileMoveIcon />
              </IconButton>
            </Tooltip>
          )}
        />
        <Can
          perform="dataset:update"
          yes={() => (
            <Tooltip title="Edit" arrow hidden={dataset?.remote_dataset}>
              <IconButton
                id={`edit-dataset-${dataset?.name}`}
                data-attr={`datasets:btn:edit-dataset-${dataset?.id}`}
                size="small"
                onClick={onEdit}
              >
                <SettingsOutlined />
              </IconButton>
            </Tooltip>
          )}
        />
        <Can
          perform="dataset:update"
          yes={() => (
            <Tooltip title="Clear" arrow>
              <IconButton
                id={`clear-dataset-${dataset?.name}`}
                data-attr={`datasets:btn:clear-dataset-${dataset?.id}`}
                size="small"
                onClick={onClear}
              >
                <ClearAllOutlined />
              </IconButton>
            </Tooltip>
          )}
        />
        <Can
          perform="dataset:delete"
          yes={() => (
            <Tooltip title="Delete" arrow>
              <IconButton
                id={`delete-dataset-${dataset?.name}`}
                data-attr={`datasets:btn:delete-dataset-${dataset?.id}`}
                size="small"
                onClick={onDelete}
              >
                <DeleteOutlineOutlined />
              </IconButton>
            </Tooltip>
          )}
        />
      </Stack>
      {openUpdateModal && (
        <UpdateDatasetModal
          id={dataset?.id}
          datasetName={dataset?.name}
          datasetDescription={dataset?.description}
          datasetTags={dataset?.tags}
          isVisible={openUpdateModal}
          onClose={() => setOpenUpdateModal(false)}
        />
      )}
      {openClearModal && (
        <ClearDatasetModal
          id={dataset?.id}
          isVisible={openClearModal}
          datasetName={dataset?.name}
          onClose={() => setOpenClearModal(false)}
        />
      )}
      <DeleteDatasetModal
        id={dataset?.id}
        isVisible={openDeleteModal}
        datasetName={dataset?.name}
        onClose={() => setOpenDeleteModal(false)}
      />
      {openFolderMappingModal && (
        <DatasetFolderMappingModal
          id={dataset?.id}
          isVisible={openFolderMappingModal}
          datasetName={dataset?.name}
          onClose={() => setOpenFolderMappingModal(false)}
        />
      )}
    </Box>
  );
};

type EllipsisButtonProps = DatasetModalControls & {
  dataset: any;
  options: any[];
};

const EllipsisButton = ({
  dataset,
  options,
  openUpdateModal,
  setOpenUpdateModal,
  openClearModal,
  setOpenClearModal,
  openDeleteModal,
  setOpenDeleteModal,
}: EllipsisButtonProps) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <IconButton
        aria-label="more"
        aria-controls={open ? "ellipsis-menu" : undefined}
        aria-haspopup="true"
        onClick={handleClick}
        size="small"
        sx={{
          padding: 0.5, // Reduce padding around the icon
          height: "fit-content", // Make height fit the content
        }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>

      <Menu
        id="ellipsis-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {options.map((option: any, index: number) => {
          const menuItem = (
            <MenuItem
              key={index}
              onClick={() => {
                option.onClick();
                handleClose();
              }}
              id={
                option.id ??
                `${option.label.toLowerCase()}-dataset-${dataset?.name}`
              }
              data-testid={option.id}
              disabled={option.disabled?.(dataset)}
            >
              {option.icon && (
                <option.icon fontSize="small" style={{ marginRight: 8 }} />
              )}
              {option.label}
            </MenuItem>
          );
          // Owners see their option directly; otherwise it's gated on the
          // RBAC capability (matches DatasetCardActionsMenu and
          // DatasetHeaderActions). The bypass must skip `Can` entirely —
          // `Can` treats an undefined perform as a deny, not a pass.
          if (option.skipPermissionCheck) {
            return menuItem;
          }
          return (
            <Can key={index} perform={option.perform} yes={() => menuItem} />
          );
        })}
      </Menu>
      {openUpdateModal && (
        <UpdateDatasetFolderModal
          folderId={dataset?.id}
          dataset={dataset}
          isVisible={openUpdateModal}
          onClose={() => setOpenUpdateModal(false)}
        />
      )}
      <ClearDatasetModal
        id={dataset.id}
        dataset={dataset}
        isFolder={Boolean(dataset?.datasets)}
        isVisible={openClearModal}
        datasetName={dataset?.name}
        onClose={() => setOpenClearModal(false)}
      />
      <DeleteDatasetModal
        id={dataset.id}
        isVisible={openDeleteModal}
        isFolder={Boolean(dataset?.datasets)}
        datasetName={dataset?.name}
        onClose={() => setOpenDeleteModal(false)}
      />
    </>
  );
};

type DatasetCardMoreActionProps = {
  dataset: any;
  isFolder?: boolean;
  atomResults?: boolean;
};
const DatasetCardMoreAction = ({
  dataset,
  isFolder = false,
  atomResults = false,
}: DatasetCardMoreActionProps) => {
  const history = useHistory();
  // Select just the stable setter — a bare useChatStore() subscribes to the
  // whole store and re-renders every card on any chat-store change.
  const setDatasets = useChatStore((state) => state.setDatasets);
  const [openUpdateModal, setOpenUpdateModal] = useState(false);
  const [openClearModal, setOpenClearModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openFolderMappingModal, setOpenFolderMappingModal] = useState(false);
  const [openShareFolder, setOpenShareFolder] = useState(false);
  const currentUser = useGetUserDetails();
  const isFolderOwner =
    !!currentUser && dataset?.create_user_fk === currentUser.id;
  const { canIt: isAdmin } = usePermission("admin");

  const handleUpdateDataset = () => {
    setOpenUpdateModal(true);
  };
  const handleManageFolderAccess = () => {
    setOpenShareFolder(true);
  };
  const handleClearDataset = () => {
    setOpenClearModal(true);
  };
  const handleDeleteDataset = () => {
    setOpenDeleteModal(true);
  };
  const handleFolderMappingDataset = () => {
    setOpenFolderMappingModal(true);
  };

  const handleChatWithFolder = () => {
    // Get all datasets from the folder
    const folderDatasets = dataset?.datasets || [];

    // Set the datasets in the AtomAssist store
    setDatasets(
      folderDatasets.map((ds: any) => ({
        id: ds.id,
        name: ds.name,
        description: ds.description,
      })),
    );

    // Set an initial message to guide the user
    // TODO: This was broken but merging to get the core feature in
    // setInitialUserMessage(
    //   `I'd like to chat about the datasets in folder: ${dataset.name}`
    // );

    // Navigate to AtomAssist with folder info in state
    history.push({
      pathname: "/assistant",
      state: {
        preloadDatasets: folderDatasets.map((ds: any) => ({
          id: ds.id,
          name: ds.name,
          description: ds.description,
        })),
      },
    });
  };
  const starMutation = useStarDataset();
  const unstarMutation = useUnstarDataset();

  const handleStarClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (dataset?.is_starred_by_user) {
      unstarMutation.mutate(dataset?.id);
    } else {
      starMutation.mutate(dataset?.id);
    }
  };

  return (
    <Box>
      {isFolder ? (
        <EllipsisButton
          dataset={dataset}
          openUpdateModal={openUpdateModal}
          setOpenUpdateModal={setOpenUpdateModal}
          openClearModal={openClearModal}
          setOpenClearModal={setOpenClearModal}
          openDeleteModal={openDeleteModal}
          setOpenDeleteModal={setOpenDeleteModal}
          options={[
            {
              label: "Chat with Folder",
              icon: ChatOutlined,
              onClick: handleChatWithFolder,
              perform: "dataset_data:read",
              id: `ds-chat-folder-btn-${dataset?.id}`,
            },
            {
              label: "Edit Folder",
              icon: EditOutlined,
              onClick: handleUpdateDataset,
              perform: "dataset:update",
              disabled: (ds: any) => ds?.remote_dataset,
            },
            {
              label: "Manage access",
              icon: PersonAddAlt1Outlined,
              onClick: handleManageFolderAccess,
              perform: "dataset:update",
              // The folder owner manages access regardless of RBAC role,
              // matching every other Manage-access surface (PR #2182 review:
              // an owner without dataset:update saw the option on the folder
              // page but not in this card menu).
              skipPermissionCheck: isFolderOwner,
              id: `ds-folder-manage-access-${dataset?.id}`,
            },
            {
              label: "Delete Folder",
              icon: DeleteOutlineOutlined,
              onClick: handleDeleteDataset,
              perform: "dataset:delete",
            },
          ]}
        />
      ) : (
        <DatasetNoFolder
          dataset={dataset}
          atomResults={atomResults}
          openUpdateModal={openUpdateModal}
          setOpenUpdateModal={setOpenUpdateModal}
          openClearModal={openClearModal}
          setOpenClearModal={setOpenClearModal}
          openDeleteModal={openDeleteModal}
          setOpenDeleteModal={setOpenDeleteModal}
          openFolderMappingModal={openFolderMappingModal}
          setOpenFolderMappingModal={setOpenFolderMappingModal}
          onStarClick={handleStarClick}
          onMoveToFolder={handleFolderMappingDataset}
          onEdit={handleUpdateDataset}
          onClear={handleClearDataset}
          onDelete={handleDeleteDataset}
        />
      )}{" "}
      {openShareFolder && (
        <ShareDialog
          open={openShareFolder}
          resource={{
            type: "datasetFolder",
            id: dataset?.id,
            name: dataset?.name,
          }}
          canTransferOwnership={isFolderOwner || isAdmin}
          onClose={() => setOpenShareFolder(false)}
        />
      )}
    </Box>
  );
};
export default DatasetCard;
